import React, { FC, useEffect, useState } from 'react'
import { change, Field, initialize, InjectedFormProps, reduxForm } from 'redux-form'
import { Button, Col, Collapse, Form, Row } from 'antd'
import { useTranslation } from 'react-i18next'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

// reducers
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'
import ColorPickerField from '../../../atoms/ColorPickerField'
import TypeRadioGroupField from '../../../atoms/TypeRadioGroupField'
import SelectField from '../../../atoms/SelectField'
import SwitchField from '../../../atoms/SwitchField'

// interfaces
import { IFormItemData } from '../../../types/interfaces'

// utils
import {
	ELEMENT_ATTRIBUTES,
	ELEMENT_TYPES,
	FORM,
	PROTECTION_INTERVAL_FOR_SAVING_ELEMENT,
	RADIO_BTN_GROUP_FIELD,
	TABLE_PROCEDURE_ATTRIBUTES
} from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

// modals
import SQLEditorModal from '../../modals/SQLEditorModal'
import ValidationModal from '../../modals/ValidationModal'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
export interface Item {
	id: number
	value: string
	label: string
}

export interface ContainerState {
	cards: Item[]
}

type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

const TableElementForm: FC<Props> = (props) => {
	const { t } = useTranslation()
	const dispatch: any = useDispatch()
	const { handleSubmit, onRemoveItem } = props
	const [isSQLEditorModalShowed, setSQLEditorModalShowed] = useState(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const validableComponents = useSelector((state: RootState) => state.formTemplate.validableComponents)
	const formItemId = useSelector((state: RootState) => state.form?.[FORM.TABLE_ELEMENT_FORM]?.values?.i)
	const canAddRows = useSelector((state: RootState) => state.form?.[FORM.TABLE_ELEMENT_FORM]?.values?.[ELEMENT_ATTRIBUTES.OPERATOR_EDITABLE])
	const procedures = useSelector((state: RootState) => state.formTemplate.procedures)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.TABLE_ELEMENT_FORM]?.values)
	const [isValidationModalShowed, setValidationModalShowed] = useState(false)

	// clear stored procedure when operator editable changes to false
	useEffect(() => {
		if (!canAddRows) {
			// clear stored procedure
			dispatch(change(FORM.TABLE_ELEMENT_FORM, ELEMENT_ATTRIBUTES.ADD_STORED_PROCEDURE, ''))
		}
	}, [canAddRows, dispatch])

	return (
		<>
			<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
				<Row className={'mx-4 mb-4'} justify={'space-between'}>
					<h3>{t('loc:Table')}</h3>
					<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
				</Row>
				<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
					<Panel header={t('loc:Label')} key='1'>
						<Field component={InputField} placeholder={t('loc:Enter label')} name={ELEMENT_ATTRIBUTES.LABEL} size={'medium'} disabled={disable} />
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col className={'h-34px'} span={11}>
								<Field component={ColorPickerField} name={ELEMENT_ATTRIBUTES.LABEL_COLOR} size={'medium'} disabled={disable} />
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									component={TypeRadioGroupField}
									optionsType={RADIO_BTN_GROUP_FIELD.TEXT_TYPE}
									name={ELEMENT_ATTRIBUTES.LABEL_TEXT_TYPE}
									disabled={disable}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Table content')} key='2'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setSQLEditorModalShowed(true)
								dispatch(
									initialize(FORM.SQL_EDITOR_FORM, {
										[ELEMENT_ATTRIBUTES.SQL]: formValues?.[ELEMENT_ATTRIBUTES.SQL] || '',
										[ELEMENT_ATTRIBUTES.LINKED_FIELDS]: formValues?.[ELEMENT_ATTRIBUTES.LINKED_FIELDS]
									})
								)
							}}
						>
							{t('loc:SQL editor')}
						</Button>
						<Row justify={'center'} className={'mt-2 w-full'}>
							<Col span={16}>
								<p className={'leading-6'}>{t('loc:Operator can add a row')}</p>
							</Col>
							<Col span={4} />
							<Col span={4}>
								<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.OPERATOR_EDITABLE} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						{canAddRows && (
							<Row className={'mb-2'} justify={'center'}>
								<Col span={24}>
									<Field
										component={SelectField}
										label={t('loc:Add stored procedure')}
										name={ELEMENT_ATTRIBUTES.ADD_STORED_PROCEDURE}
										options={procedures.data}
										size={'medium'}
										disabled={disable}
										clearBtn={true}
										loading={procedures.isLoading}
									/>
								</Col>
							</Row>
						)}
					</Panel>
					<Panel header={t('loc:Size')} key='3'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11}>
								<Field
									className={'element-width'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.COL}
									size={'medium'}
									suffix={<WidthIcon />}
									disabled={disable}
									maxLength={2}
								/>
							</Col>
							<Col span={2} />
							<Col span={11}>
								<Field
									className={'element-height'}
									component={InputField}
									name={ELEMENT_ATTRIBUTES.ROW}
									size={'medium'}
									suffix={<HeightIcon />}
									disabled={disable}
									maxLength={3}
								/>
							</Col>
						</Row>
					</Panel>
					<Panel header={t('loc:Validations')} key='4'>
						<Button
							className={'unfilled-btn w-full h-25px mb-2'}
							disabled={disable}
							onClick={() => {
								setValidationModalShowed(true)
							}}
						>
							{t('loc:Validation options')}
						</Button>
					</Panel>
					<Panel header={t('loc:Procedure')} key='5'>
						<Row justify={'center'} className={'my-2 w-full'}>
							<Col span={11} className={'h-34px flex flex-col justify-center'}>
								<p className={'m-0'}>{t('loc:Active')}</p>
							</Col>
							<Col span={2} />
							<Col span={11} className={'h-34px'}>
								<Field component={SwitchField} name={TABLE_PROCEDURE_ATTRIBUTES.ALLOW_PROCEDURE} className={'float-right'} disabled={disable} />
							</Col>
						</Row>
						{formValues?.[TABLE_PROCEDURE_ATTRIBUTES.ALLOW_PROCEDURE] && (
							<div className={'mb-2'}>
								<Col span={24}>
									{' '}
									<Field
										component={SelectField}
										label={t('loc:Store procedure')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										options={procedures.data}
										loading={procedures.isLoading}
										name={TABLE_PROCEDURE_ATTRIBUTES.PROCEDURE}
										placeholder={t('loc:Select procedure')}
										size={'medium'}
										disabled={disable}
										clearBtn={true}
									/>{' '}
								</Col>
								<Col span={24}>
									{' '}
									<Field
										component={SelectField}
										label={t('loc:Fields')}
										getPopupContainer={(triggerNode: any) => triggerNode}
										options={validableComponents?.data}
										mode={'multiple'}
										name={TABLE_PROCEDURE_ATTRIBUTES.FIELDS_LIST}
										placeholder={t('loc:Select field')}
										size={'medium'}
										disabled={disable}
										clearBtn={true}
									/>{' '}
								</Col>
							</div>
						)}
					</Panel>
				</Collapse>
			</Form>
			<SQLEditorModal
				width={460}
				typeOfElementForm={FORM.TABLE_ELEMENT_FORM}
				isVisible={isSQLEditorModalShowed}
				onCancel={() => {
					setSQLEditorModalShowed(false)
				}}
			/>
			<ValidationModal
				width={460}
				typeOfElementForm={FORM.TABLE_ELEMENT_FORM}
				isVisible={isValidationModalShowed}
				onCancel={() => {
					setValidationModalShowed(false)
				}}
				elementLabel={formValues?.label}
				elementType={ELEMENT_TYPES.TABLE}
			/>
		</>
	)
}

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.TABLE_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(TableElementForm)

export default form
